import { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { calculateCompoundInterest } from '../../utils/calculations';
import { getTaxConfig, INVESTMENT_TYPES } from '../../utils/taxEngine';
import { formatCurrency, exportToCSV } from '../../utils/formatters';
import { TableIcon, Download, FileText, PauseCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const BreakdownTable = () => {
  const { inputs, t, language, formatMoney, currency } = useApp();
  
  // Calculate breakdown data
  const breakdownData = useMemo(() => {
    const taxConfig = getTaxConfig(inputs.investmentType);
    const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
    
    const calcParams = {
      initialDeposit: inputs.initialDeposit,
      monthlyContribution: inputs.monthlyContribution,
      annualInterestRate: inputs.annualInterestRate,
      years: inputs.years,
      compoundingFrequency: inputs.compoundingFrequency,
      contributionFrequency: inputs.contributionFrequency,
      taxRate: inputs.includeTaxes ? taxConfig.interestTaxRate : 0,
      taxOnEachPeriod: taxConfig.taxOnEachPeriod,
      adminFeeRate: isUnitLinked ? inputs.adminFeeRate : 0,
      pauseStartYear: inputs.pauseEnabled ? inputs.pauseStartYear : 0,
      pauseEndYear: inputs.pauseEnabled ? inputs.pauseEndYear : 0,
      useSimpleInterest: taxConfig.useSimpleInterest,
    };
    
    const result = calculateCompoundInterest(calcParams);
    return result.yearlyBreakdown;
  }, [inputs]);
  
  const isUnitLinked = inputs.investmentType === INVESTMENT_TYPES.UNIT_LINKED;
  
  // Export to CSV
  const handleExportCSV = () => {
    const headers = [
      { key: 'year', label: t('yearColumn') },
      { key: 'totalContributions', label: t('totalContributions') },
      { key: 'interestNet', label: inputs.includeTaxes ? t('interestNet') : t('yearInterest') },
      ...(inputs.includeTaxes ? [{ key: 'taxPaid', label: t('taxPaid') }] : []),
      ...(isUnitLinked ? [{ key: 'adminFee', label: t('adminFee') }] : []),
      { key: 'endBalance', label: t('totalColumn') },
      ...(inputs.pauseEnabled ? [{ key: 'isPaused', label: t('paused') }] : []),
    ];
    
    const data = breakdownData.map(row => ({
      ...row,
      interestNet: row.totalInterestGross - row.totalTaxPaid,
      isPaused: row.isPaused ? (language === 'ro' ? 'Da' : 'Yes') : '',
    }));
    
    const filename = language === 'ro' ? 'calcul-dobanda' : 'interest-calculation';
    exportToCSV(data, filename, headers, language);
  };
  
  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    
    // Title
    const title = language === 'ro' ? 'Raport Calcul Dobândă' : 'Interest Calculation Report';
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 20);
    
    // Summary info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const investmentTypeName = inputs.investmentType === INVESTMENT_TYPES.SAVINGS_ACCOUNT 
      ? (language === 'ro' ? 'Cont de economii' : 'Savings Account')
      : 'Unit Linked';
    
    const summaryLines = [
      `${t('investmentType')}: ${investmentTypeName}`,
      `${t('initialDeposit')}: ${formatCurrency(inputs.initialDeposit)}`,
      `${t('monthlyContribution')}: ${formatCurrency(inputs.monthlyContribution)}`,
      `${t('annualInterestRate')}: ${inputs.annualInterestRate}%`,
      `${t('investmentPeriod')}: ${inputs.years} ${language === 'ro' ? 'ani' : 'years'}`,
    ];
    
    if (isUnitLinked && inputs.adminFeeRate > 0) {
      summaryLines.push(`${t('adminFee')}: ${inputs.adminFeeRate}%`);
    }
    
    if (inputs.pauseEnabled) {
      summaryLines.push(`${t('contributionPause')}: ${language === 'ro' ? 'An' : 'Year'} ${inputs.pauseStartYear} - ${inputs.pauseEndYear}`);
    }
    
    let yPos = 30;
    summaryLines.forEach(line => {
      doc.text(line, 14, yPos);
      yPos += 6;
    });
    
    // Final value highlight
    const lastRow = breakdownData[breakdownData.length - 1];
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`${t('finalValue')}: ${formatCurrency(lastRow.endBalance)}`, 14, yPos + 5);
    
    // Table
    const tableColumn = [
      t('yearColumn'),
      t('totalContributions'),
      inputs.includeTaxes ? t('interestNet') : t('yearInterest'),
      ...(inputs.includeTaxes ? [t('taxPaid')] : []),
      ...(isUnitLinked ? [t('adminFee')] : []),
      t('totalColumn'),
    ];
    
    const tableRows = breakdownData.map(row => {
      const rowData = [
        row.year + (row.isPaused ? ' *' : ''),
        formatCurrency(row.totalContributions),
        formatCurrency(row.totalInterestGross - row.totalTaxPaid),
      ];
      
      if (inputs.includeTaxes) {
        rowData.push(formatCurrency(row.totalTaxPaid));
      }
      
      if (isUnitLinked) {
        rowData.push(formatCurrency(row.adminFee || 0));
      }
      
      rowData.push(formatCurrency(row.endBalance));
      
      return rowData;
    });
    
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: yPos + 15,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      styles: { fontSize: 9 },
    });
    
    // Footer with pause legend
    if (inputs.pauseEnabled) {
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text(`* ${t('paused')} - ${t('pauseDescription')}`, 14, finalY);
    }
    
    // Disclaimer
    const disclaimerY = doc.lastAutoTable.finalY + (inputs.pauseEnabled ? 20 : 10);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(t('taxDisclaimer'), 14, disclaimerY);
    
    const filename = language === 'ro' ? 'raport-calcul-dobanda.pdf' : 'interest-calculation-report.pdf';
    doc.save(filename);
  };
  
  return (
    <Card 
      className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden"
      data-testid="breakdown-table"
    >
      <CardHeader className="pb-4 border-b border-border/50">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-3 text-xl font-light tracking-tight">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/10 to-emerald-500/10">
              <TableIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            {t('breakdownTable')}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              className="h-9 px-4 rounded-full hover:bg-blue-500/10 hover:border-blue-500/50 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('exportCSV')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              className="h-9 px-4 rounded-full hover:bg-emerald-500/10 hover:border-emerald-500/50 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all duration-200"
              data-testid="export-pdf-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              {t('exportPDF')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 w-20">
                  {t('yearColumn')}
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right">
                  {t('totalContributions')}
                </TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right">
                  {inputs.includeTaxes ? t('interestNet') : t('yearInterest')}
                </TableHead>
                {inputs.includeTaxes && (
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right">
                    {t('taxPaid')}
                  </TableHead>
                )}
                {isUnitLinked && (
                  <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right">
                    {t('adminFee')}
                  </TableHead>
                )}
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-foreground/70 text-right">
                  {t('totalColumn')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {breakdownData.map((row, index) => {
                const isLastRow = index === breakdownData.length - 1;
                return (
                  <TableRow 
                    key={row.year}
                    className={`
                      transition-colors duration-200
                      ${isLastRow ? 'bg-gradient-to-r from-blue-100 to-emerald-100 dark:from-blue-900/30 dark:to-emerald-900/30 font-semibold' : 'hover:bg-muted/50'}
                      ${row.isPaused ? 'bg-orange-50 dark:bg-orange-900/20' : ''}
                    `}
                    data-testid={`table-row-${row.year}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {row.year}
                        {row.isPaused && (
                          <Badge variant="outline" className="text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300">
                            <PauseCircle className="h-3 w-3 mr-1" />
                            {t('paused')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(row.totalContributions)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                      {formatMoney(row.totalInterestGross - row.totalTaxPaid)}
                    </TableCell>
                    {inputs.includeTaxes && (
                      <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">
                        {formatMoney(row.totalTaxPaid)}
                      </TableCell>
                    )}
                    {isUnitLinked && (
                      <TableCell className="text-right tabular-nums text-purple-600 dark:text-purple-400">
                        {formatMoney(row.adminFee || 0)}
                      </TableCell>
                    )}
                    <TableCell className={`text-right tabular-nums ${isLastRow ? 'text-blue-700 dark:text-blue-300 font-bold' : ''}`}>
                      {formatMoney(row.endBalance)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default BreakdownTable;
